import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { FiBook, FiSearch, FiPlus, FiRefreshCcw, FiEdit, FiTrash2, FiEye } from 'react-icons/fi'
import { fetchCourses } from '../../store/slices/adminSlice'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'
import Select from '../../components/Select/Select'
import './Courses.css'

const Courses = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { courses, isLoading, error } = useSelector((state) => state.admin)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    loadCourses()
  }, [page, statusFilter])

  const loadCourses = async () => {
    const params = {
      page,
      limit: 25,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    dispatch(fetchCourses(params))
  }


  const handleDelete = async () => {
    if (!selectedCourse) return
    setDeleteLoading(true)
    try {
      await adminApi.delete(`/courses/${selectedCourse._id}`)
      setShowDeleteModal(false)
      setSelectedCourse(null)
      loadCourses()
    } catch (err) {
      console.error('Delete failed:', err)
    } finally {
      setDeleteLoading(false)
    }
  }

  const filteredCourses = useMemo(() => {
    const coursesList = courses?.courses || courses?.data || []
    if (!Array.isArray(coursesList)) return []
    const query = searchQuery.toLowerCase()
    return coursesList.filter((course) => {
      const matchesSearch =
        !query ||
        [course.title, course.description, course.level]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      return matchesSearch
    })
  }, [courses, searchQuery])

  const columns = [
    {
      key: 'title',
      label: 'Course',
      render: (row) => (
        <div>
          <strong>{row.title}</strong>
          <span className="course-level">{row.level}</span>
        </div>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      render: (row) => `₹${row.price || 0}`,
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (row) => row.duration || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'active'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="course-actions">
          <Link to={`/courses/${row._id}`} className="action-link">
            View Details
          </Link>
          <button
            type="button"
            className="action-btn delete"
            onClick={() => {
              setSelectedCourse(row)
              setShowDeleteModal(true)
            }}
            title="Delete course"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="courses-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Course Management</p>
          <h1>All Courses</h1>
          <p className="subhead">Manage courses and programs</p>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate('/courses/new')}>
            <FiPlus size={18} />
            Add Course
          </Button>
          <button
            type="button"
            className="refresh-btn"
            onClick={loadCourses}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search courses"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'disabled', label: 'Disabled' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredCourses}
        loading={isLoading}
        emptyMessage="No courses found"
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedCourse(null)
        }}
        title="Delete Course"
        size="small"
      >
        <p>Are you sure you want to delete {selectedCourse?.title}?</p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default Courses

